import { FieldExtensionComponentProps } from "@backstage/plugin-scaffolder-react";
import { ErrorPanel, Progress } from '@backstage/core-components';
import { z } from 'zod';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { useEffect, useRef } from "react";
import { Typography } from '@material-ui/core';
import { useAsync } from 'react-use';
import {
	fetchApiRef,
	useApi,
} from '@backstage/core-plugin-api';
import { EntraOAuthWrapper } from "../EntraOAuthWrapper/EntraOAuthWrapper";
import { useEntraOAuthContext } from "../../hooks/useEntraOAuthContext";
import { fetchUserAdoAccess, substituteTemplate } from "../helpers";

const uiOptionsSchema = z.object({
    organization: z.string().describe('The Azure DevOps organization to query'),
    project: z.string().describe('The Azure DevOps project to query'),
    pipelineId: z.string().describe('The Azure DevOps pipeline ID to query'),
    environmentName: z.string().optional().describe('The Azure DevOps environment name to check access for'),
    sessionKey: z.string().optional().describe('The key used for storing session token.').default('adToken'),
    extraScopes: z.array(z.string()).optional().describe('Additional OAuth scopes to request'),
    title: z.string().optional().describe('The title to display for the field').default("Pipeline Access")
});

export const AdoPipelineAccessFieldSchema = makeFieldSchema({
	output: x => x.string(),
	uiOptions: zImpl => zImpl.object({
    organization: zImpl.string().describe('The Azure DevOps organization to query'),
    project: zImpl.string().describe('The Azure DevOps project to query'),
    pipelineId: zImpl.string().describe('The Azure DevOps pipeline ID to query'),
    environmentName: zImpl.string().optional().describe('The Azure DevOps environment name to check access for'),
    sessionKey: zImpl.string().optional().describe('The key used for storing session token.').default('adToken'),
    extraScopes: zImpl.array(zImpl.string()).optional().describe('Additional OAuth scopes to request'),
    title: zImpl.string().optional().describe('The title to display for the field').default("Pipeline Access")
  }),
});

const AdoPipelineAccessFieldScopes = ['https://app.vssps.visualstudio.com/vso.build_execute'];
const AdoPipelineEnvironmentAccessFieldScopes = ['https://app.vssps.visualstudio.com/vso.graph'];

export const AdoPipelineAccessField = (props: FieldExtensionComponentProps<string, {
    organization: string,
    project: string,
    pipelineId: string,
    environmentName?: string,
    sessionKey?: string,
    title?: string,
    extraScopes?: string[]}>) => {

    const result = uiOptionsSchema.safeParse(props.uiSchema['ui:options']);

    if (!result.success) {
        return <ErrorPanel error={result.error} />;
    }

    const { extraScopes, title, environmentName, ...uiOptions } = result.data;
    const scopes = Array.from(new Set(AdoPipelineAccessFieldScopes.concat(extraScopes ?? [])));
    if (environmentName) {
        scopes.push(...AdoPipelineEnvironmentAccessFieldScopes);
    }

    return (
        <EntraOAuthWrapper
            {...props}
            scopes={scopes}
            title={title ?? props.name}
            sessionKey={uiOptions.sessionKey}
        >
            <AdoPipelineAccessFieldComponent {...props} uiOptions={uiOptions} />
        </EntraOAuthWrapper>
    );
};

const AdoPipelineAccessFieldComponent = (
    props: FieldExtensionComponentProps<string>
) => {
    const fetchApi = useApi(fetchApiRef);
    const { onChange, formData } = props;
    const { formData: allFormData } = props.formContext;
    const organization = String(props.uiSchema['ui:options']?.organization);
    const project = String(props.uiSchema['ui:options']?.project);
    const pipelineId = String(props.uiSchema['ui:options']?.pipelineId);
    const { token } = useEntraOAuthContext();
    const prevFormDataRef = useRef<string>();

    const { result: environmentName, isValid }  = substituteTemplate<string>(
        String(props.uiSchema['ui:options']?.environmentName  ?? ''),
        allFormData,
        (value) => String(value)
    );

    useEffect(() => {
        if (prevFormDataRef.current === formData) {
            return;
        }

        if (formData !== undefined) {
            onChange(undefined);
        }
        else {
            prevFormDataRef.current = formData;
        }
    }, [environmentName, isValid, token]);

    const { value: accessResult, loading } = useAsync(async () => {
        if (!isValid || !token) {
            return undefined;
        }
        return fetchUserAdoAccess(project, organization, pipelineId, token, fetchApi, environmentName);
    }, [environmentName, token]);

    useEffect(() => {
        if (!loading && accessResult) {
            onChange(accessResult.canApprove ? 'true' : undefined);
        }
    }, [accessResult, loading]);

    if (loading) {
		return <Progress />;
	}
    if (accessResult?.error) {
		return <ErrorPanel error={new Error(accessResult.error)} />;
	}
    if(accessResult?.canApprove) {
        return <Typography>✅ Sufficient permissions to run the pipeline</Typography>;
    }
    return null;
};
